import moment from 'moment';

import { getSession } from '../../extensions/listing-page/utils/listing.utils/getSession';

const SESSION_HORIZON_DAYS = 365;

// buildSessionPairs turns a flat list of listings into an array of
// { listing, sessionDate } pairs — one pair per upcoming session occurrence.
//
// Two listing types are handled differently:
//
//   Non-recurring (isRecurring absent or 'no'):
//     The listing has a single session defined by `firstSession` (a date string,
//     e.g. "2025-03-10") and `startTime` (e.g. "09:30"). We combine them into a
//     moment and include the pair only if the session hasn't already passed.
//
//   Recurring:
//     We delegate to `getSession`, which expands the recurrence rule stored in
//     publicData into an array of session objects. Each object has a `rawDate`
//     moment. We include pairs whose rawDate falls within [today, horizon] —
//     the horizon caps the look-ahead at SESSION_HORIZON_DAYS days to avoid
//     generating thousands of entries for long-running recurring sessions.
//
// The result is a flat array that can then be grouped (by week, by location, etc).
export const buildSessionPairs = (listings) => {
  const pairs = [];
  const today = moment().startOf('day');
  const horizon = moment().add(SESSION_HORIZON_DAYS, 'days');

  listings.forEach((listing) => {
    const { publicData } = listing.attributes || {};
    if (!publicData) return;

    const { isRecurring, firstSession, startTime } = publicData;

    if (!isRecurring || isRecurring === 'no') {
      // Single-session listing: build one pair from the fixed date + time fields.
      if (firstSession && startTime) {
        const [hours, minutes] = startTime.split(':').map(Number);
        const sessionDate = moment(firstSession).startOf('day').hours(hours).minutes(minutes);
        if (sessionDate.isSameOrAfter(today, 'day')) {
          pairs.push({ listing, sessionDate });
        }
      }
    } else {
      // Recurring listing: expand all occurrences and filter to the upcoming window.
      // firstDayOfWeek: 1 tells getSession to treat Monday as the start of the week,
      // which keeps week boundaries consistent with the groupByWeek logic.
      const sessions = getSession({ ...publicData, firstDayOfWeek: 1 });
      sessions.forEach(({ rawDate }) => {
        if (
          rawDate &&
          rawDate.isSameOrAfter(today, 'day') &&
          rawDate.isSameOrBefore(horizon, 'day')
        ) {
          pairs.push({ listing, sessionDate: rawDate });
        }
      });
    }
  });

  return pairs;
};

export default buildSessionPairs;
